import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Global admin stats fetched ONCE from /api/dashboard/stats and shared with
// BottomTabBar's pending-badge + AdminPanel's KPI cards.
//
// Fixing the previous StrictMode double-mount race: prior to this context,
// each consumer used a local useEffect and a `cancelled` flag. React StrictMode
// remounts effects in dev, and with a slow ~5s backend response the FIRST
// closure sees cancelled=true when the response arrives → setState is skipped
// → badge never renders. A single context provider fetches once (guarded by
// a ref, immune to StrictMode double-invocations) and re-fetches every 30s.

const AdminStatsCtx = createContext({
  stats: null,
  loading: false,
  refresh: () => {},
});

export function AdminStatsProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.role === "admin");

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refs survive StrictMode's double-invocation; regular state does not.
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAdmin || inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      if (mountedRef.current) setStats(data);
    } catch (e) {
      console.warn("[AdminStats] fetch failed:", e?.response?.status || e?.message);
      // Retry once after 1.5s in case of transient auth/hydration race.
      setTimeout(() => {
        inFlightRef.current = false;
        if (mountedRef.current && isAdmin) refresh();
      }, 1500);
      return;
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [authLoading, isAdmin, refresh]);

  return (
    <AdminStatsCtx.Provider value={{ stats, loading, refresh }}>
      {children}
    </AdminStatsCtx.Provider>
  );
}

export function useAdminStats() {
  return useContext(AdminStatsCtx);
}
